  // src/components/BuildingInfoOverlay.jsx
  import React, { useState, useEffect, useMemo } from "react";
  import "./BuildingInfoOverlay.css";

 // 🔹 호선 문자열 정규화: "2호선" → "2", "신림선" → "신림"
 function normalizeLineName(line) {
   if (!line) return null;
   const num = line.match(/\d+/);
   if (num) return num[0];       // "2호선" → "2", "09" → "9"

   if (line.includes("신림")) return "신림";
   if (line.includes("분당")) return "수인분당";
   if (line.includes("수인")) return "수인분당";
   if (line.includes("경의") || line.includes("중앙"))
     return "경의중앙";

   return line;
 }

  // 🔹 1. 공통 정렬 메타데이터 생성 함수 (컴포넌트 외부로 통합)
  function buildHoSortMeta(hoNm) {
    const raw = String(hoNm ?? "").trim();
    const numPart = raw.match(/\d+/)?.[0] ?? "";

    let priority = 2; // 기본: 기타 텍스트
    if (raw.startsWith("지")) priority = 0;
    else if (/^\d/.test(raw)) priority = 1;
    else if (raw.startsWith("옥") || raw.startsWith("다")) priority = 3;

    let keyNum = numPart ? numPart.padStart(6, "0") : "999999";

    return {
      raw,
      key: Number(keyNum),
      priority,
    };
  }

function buildOverridePreview(applyData) {
  const rows = [];

  if (applyData?.building) {
    rows.push(["건물명", applyData.building.name]);
    rows.push(["동/호/층", `${applyData.building.dong || "-"} / ${applyData.building.ho || "-"} / ${applyData.building.floor ?? "-"}`]);
    rows.push(["준공일", applyData.building.completionDate || "-"]);
  }
  if (applyData?.area) {
    rows.push(["전용/공용/대지(㎡)",
      `${applyData.area.private ?? "-"} / ${applyData.area.public ?? "-"} / ${applyData.area.land ?? "-"}`]);
    rows.push(["연면적(㎡)", applyData.area.total ?? "-"]);
  }
  if (applyData?.buildingStats) {
    rows.push(["층수(지상/지하)",
      `${applyData.buildingStats.groundFloors ?? "-"} / ${applyData.buildingStats.undergroundFloors ?? "-"}`]);
    rows.push(["주차/세대/승강기",
      `${applyData.buildingStats.parkingTotal ?? "-"} / ${applyData.buildingStats.householdCount ?? "-"} / ${applyData.buildingStats.elevatorCount ?? "-"}`]);
  }
  if (applyData?.location?.station) {
    rows.push(["교통",
      `${applyData.location.station.name} · 도보 ${applyData.location.station.walkMin}분`]);
  }
  if (applyData?.location) {
    rows.push(["좌표", `${applyData.location.lat ?? "-"}, ${applyData.location.lng ?? "-"}`]);
  }
  return rows;
}


  // 문자열 정리
  //  - 앞에 한글 '비'가 붙은 호수(비01, 비1)를 지하 B 호수(B01, B1)와 동일하게 취급
  //  - 대소문자 무시
  const normalizeHo = (value) => {
    let s = (value ?? "").toString().trim();
    if (!s) return "";
    if (/^\d+$/.test(s)) return s;

    // 한글 '비'로 시작하면 B 로 치환 (지하층 표기 통일)
    // 예: "비01" -> "B01"
    s = s.replace(/^비/gi, "B");

    return s.toUpperCase();
  };

  export default function BuildingInfoOverlay({
    open,
    onClose,
    loading,
    error,
    buildingInfo,
    jibunAddress,
    roadAddress,
    lat,
    lng,
    initialSelection,
    readonly = false,
    onApply,
    onRefresh,
    cachedAt,
  }) {

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applyData, setApplyData] = useState(null);
  const previewRows = useMemo(() => buildOverridePreview(applyData), [applyData]);

  const LINE_COLORS = {
    "1": "#0052A4",
    "2": "#00A84D",
    "3": "#EF7C1C",
    "4": "#00A5DE",
    "5": "#996CAC",
    "6": "#CD7C2F",
    "7": "#747F00",
    "8": "#E6186C",
    "9": "#BDB092",
    "수인분당": "#FABE00",
    "분당": "#FABE00",
    "신분당": "#D72D37",
    "경의중앙": "#77C4A3",
    "신림": "#00498F",
  };

  // YYYYMMDD 또는 YYYY-MM-DD → YYYY년 MM월 DD일
  const formatKoreanDate = (v) => {
    if (!v) return "-";
    const s = String(v).replace(/[^0-9]/g, "");
    if (s.length !== 8) return v;
    return `${s.slice(0, 4)}년 ${s.slice(4, 6)}월 ${s.slice(6, 8)}일`;
  };
  
  // ✅ 1. building 데이터를 안전하게 가져오기 (data가 있든 없든 대응)
  const building = useMemo(() => buildingInfo?.data || buildingInfo || null, [buildingInfo]);

  // ✅ 2. recapTitle과 dongList를 building 객체에서 직접 추출
  const dongList = useMemo(() => building?.dongList || [], [building]);
  const vworldShareList = building?.vworldShareList || [];

  const [selectedDong, setSelectedDong] = useState("");
  const [vworldDong, setVworldDong] = useState("");
  const [vworldHo, setVworldHo] = useState("");
  const [showFloorDetails, setShowFloorDetails] = useState(false);

  // ✅ 3. 동 목록이 들어오면 첫 번째 동을 자동으로 선택하는 로직 강화
  useEffect(() => {
    if (building && dongList.length > 0) {
      // 현재 선택된 동이 리스트에 없거나 초기 상태일 때만 첫 번째 동으로 설정
      const exists = dongList.find(d => d.dongName === selectedDong);
      if (!selectedDong || !exists) {
        setSelectedDong(dongList[0].dongName);
        setSelectedHo(""); 
      }
    }
  }, [building, dongList]); // building 전체를 감시하여 첫 로딩 대응

  // ✅ 4. 현재 선택된 동 아이템
  const selectedDongItem = useMemo(() => {
    return dongList.find((d) => String(d.dongName) === String(selectedDong)) || null;
  }, [dongList, selectedDong]);

  // 🔹 [통합 정렬] 건축물대장 호수 정렬 (우선순위 로직 적용)
  const sortedHos = useMemo(() => {
    const list = selectedDongItem?.units || [];
    return [...list]
      .map((h) => ({ ...h, __sort: buildHoSortMeta(h.hoNm) }))
      .sort((a, b) => {
        if (a.__sort.priority !== b.__sort.priority) return a.__sort.priority - b.__sort.priority;
        return a.__sort.key - b.__sort.key;
      })
      .map(({ __sort, ...rest }) => rest);
  }, [selectedDongItem]);

  // 🔹 [Vworld 데이터 재구성] 층+호수 결합 및 루프 데이터 대응 로직
  // 🔹 [Vworld 데이터 재구성] 층+호수 결합 및 루프 데이터 대응 로직
  const processedVworldList = useMemo(() => {
    if (!building?.vworldShareRawList) return vworldShareList; // 백엔드 데이터 없으면 기존 리스트

    // PNU 19자리 → "1653-3" 형태로 (필지별 지분 표시용)
    //   시도2+시군구3+읍면동3+리2 = 10 / 산여부 1 / 본번 4 / 부번 4
    const pnuToJibun = (pnu) => {
      const s = String(pnu || "");
      if (s.length < 19) return s;
      const bun = parseInt(s.slice(11, 15), 10);
      const ji  = parseInt(s.slice(15, 19), 10);
      return ji ? `${bun}-${ji}` : String(bun);
    };

    const dongGroups = {};
    building.vworldShareRawList.forEach(item => {
      // 1. 층 정보가 없어도 지분은 살립니다.
      //    예전에는 여기서 return 으로 걸러내 해당 호가 화면에서 사라졌습니다.
      const floorLabel = (item.buldFloorNm && item.buldFloorNm !== "0000")
        ? `${item.buldFloorNm}층 ` : "";
      const hoLabel = item.buldHoNm ? `${item.buldHoNm}호` : "호미상";
      const fullHoName = `${floorLabel}${hoLabel}`.trim() || "층·호 미상";

      // 2. 동 이름 결정 (0000 이면 단일동)
      const dongName = item.buldDongNm && item.buldDongNm !== "0000"
        ? item.buldDongNm : "단일동";
      if (!dongGroups[dongName]) dongGroups[dongName] = { dongName, units: [] };

      // 3. 지분율
      const [share, total] = (item.ldaQotaRate || "").split("/");

      dongGroups[dongName].units.push({
        hoNm: fullHoName,
        landShare: share || "0",
        landTotal: total || "0",
        rawFloor: item.buldFloorNm,
        rawHo: item.buldHoNm,
        // 백엔드가 필지별 지분을 합칠 때 남겨둔 내역.
        // 건물이 여러 필지에 걸쳐 있으면 여기에 필지별 지분이 들어 있습니다.
        parcels: Array.isArray(item.parcels)
          ? item.parcels.map(p => ({ jibun: pnuToJibun(p.pnu), rate: p.rate }))
          : [],
        parcelCount: item.parcelCount || 1
      });
    });
    return Object.values(dongGroups);
  }, [building, vworldShareList]);

  // 🔹 [Vworld 정렬] 재구성된 리스트를 기준으로 정렬 적용
  const selectedVworldDongItem = useMemo(() => {
    const list = processedVworldList;
    const dong = list.find(d => d.dongName === vworldDong) || list[0] || null;
    if (!dong) return null;

    const sortedUnits = [...dong.units]
      .map((h) => ({ ...h, __sort: buildHoSortMeta(h.hoNm) }))
      .sort((a, b) => {
        // "지하" 우선 -> 숫자 순 정렬
        if (a.__sort.priority !== b.__sort.priority) return a.__sort.priority - b.__sort.priority;
        return a.__sort.key - b.__sort.key;
      })
      .map(({ __sort, ...rest }) => rest);

    return { ...dong, units: sortedUnits };
  }, [processedVworldList, vworldDong]);

  // ✅ 화면에 표시할 최종 데이터들 (building이 없을 때를 대비한 안전 장치)
  const recap = building?.recapTitle || {};
  const landArea = recap?.platArea || 0; // ✅ 경로 수정: building.landInfo.area -> recap.platArea
  const selectedVworldHoItem = useMemo(() => 
    selectedVworldDongItem?.units.find(h => h.hoNm === vworldHo) || null
  , [selectedVworldDongItem, vworldHo]);


  // ✅ building 객체를 가장 먼저 확정
  // const building = buildingInfo?.data || null; //◆응답구조 변경으로 임시 수정

  // 주소 표시용
  const displayAddress =
    roadAddress ||
    jibunAddress ||
    buildingInfo?.providers?.vworld?.address?.roadAddressFromVworld ||
    buildingInfo?.providers?.vworld?.address?.jibunAddressFromVworld ||
    "";
    
  // ✅ 지도용 좌표 fallback (vworld → building)
  const [mapLat, setMapLat] = useState(null);
  const [mapLng, setMapLng] = useState(null);

  // 🔹 건물 좌표 기반으로 DB 지하철역 조회
  useEffect(() => {
    if (!open || mapLat == null || mapLng == null) {
      setNearestStations([]);
      return;
    }

  const url = `/api/subway/nearest?lat=${mapLat}&lng=${mapLng}`;
  
  console.log("[MAP RENDER]", mapLat, mapLng);
  
    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        if (data.ok && Array.isArray(data.exits)) {
          setNearestStations(data.exits);
        } else {
          setNearestStations([]);
        }
      })
      .catch(() => setNearestStations([]));
  }, [mapLat, mapLng]);

    // 가장 가까운 지하철역 정보 (backend /api/building-info 의 stations 필드)
  const [nearestStations, setNearestStations] = useState([]);
  const nearestStation =
    Array.isArray(nearestStations) && nearestStations.length > 0
      ? [...nearestStations].sort(
          (a, b) => (a.distance_m ?? Infinity) - (b.distance_m ?? Infinity)
        )[0]
      : null;

  // 건축물대장 호수(왼쪽 select)의 정규화 값
  const [selectedHo, setSelectedHo] = useState(
    initialSelection?.ho || ""
  );
  const normalizedSelectedHo = normalizeHo(selectedHo);

  const selectedHoItem =
    sortedHos.find(
      (h) => normalizeHo(h.hoNm ) === normalizedSelectedHo
    ) || null;

  console.log("[GEOCODE]", displayAddress);
  // 🔹 주소 → 좌표 변환 (지도/이용역 계산의 기준)
  useEffect(() => {
    if (!open || !displayAddress) return;
    if (mapLat != null && mapLng != null) return;
    if (!window.kakao || !window.kakao.maps || !window.kakao.maps.services) {
      return;
    }

    const geocoder = new window.kakao.maps.services.Geocoder();

    geocoder.addressSearch(displayAddress, (result, status) => {
      if (status !== window.kakao.maps.services.Status.OK) {
        console.warn("[GEOCODE FAIL]", displayAddress, status);
        setMapLat(null);
        setMapLng(null);
        console.log("[GEO RESULT]", Number(y), Number(x));
        return;
      }

      const { y, x } = result[0]; // y: lat, x: lng
      setMapLat(Number(y));
      setMapLng(Number(x));
    });
  }, [open, displayAddress]);

  // 🔹지도 자동 로딩
  useEffect(() => {
    if (
      !open ||
      mapLat == null ||
      mapLng == null ||
      !window.kakao ||
      !window.kakao.maps
    ) {
      return;
    }

    const t = setTimeout(() => {
      const mapContainer = document.getElementById("building-info-map");
      if (mapContainer && window.kakao && window.kakao.maps) {
        mapContainer.innerHTML = "";
        const center = new window.kakao.maps.LatLng(mapLat, mapLng);

        const map = new window.kakao.maps.Map(mapContainer, {
          center,
          level: 4,
        });

        new window.kakao.maps.Marker({
          map,
          position: center,
        });
      }
    }, 300);

    return () => clearTimeout(t);
  }, [open, mapLat, mapLng]);

  console.log("[MAP CHECK]", mapLat, mapLng);

  // 🔹 Hook 다 호출한 다음에 early return
  if (!open) return null;

 // 🔹 디버그: building 좌표와 지하철역 조회 결과 출력
 console.log("BUILDING:", building);
 console.log("NEAREST STATIONS:", nearestStations);

  // 용도지역: data.go 지역지구구역(getBrJijiguInfo) 우선, VWorld는 백업
  const jijiguList = Array.isArray(building?.jijiguList)
    ? building.jijiguList
    : [];

  const landUseText = (() => {
    if (!jijiguList.length) return "-";

    const set = new Set();

    jijiguList.forEach((j) => {
      if (!j || !j.name) return;

      const text = j.etc
        ? `${j.name}(${j.etc})`
        : j.name;

      set.add(text.trim());
    });

    return set.size > 0 ? Array.from(set).join("\n") : "-";
  })();

  const buildApplyData = () => {
    if (!building) return null;

    // 🔹 floorInfo 가공
    const rawFloor = selectedHoItem ? (selectedHoItem.floorInfo || "") : "";
    let floorNumeric = null;
    const numMatch = rawFloor.match(/\d+/);
    if (numMatch) {
      floorNumeric = parseInt(numMatch[0], 10);
      if (rawFloor.includes("지하")) floorNumeric = -floorNumeric;
    }

    // [DONG_APPLY_V2] 2026-10-03 — 「일주하우스 B동」처럼 건물명+동 형태와 대지권 동까지 처리
    //   ① 표제부 동 이름 전체가 동 모양         "101동" "B동" "가동"   → 101 / B / 가
    //   ② 공백이 있으면 마지막 단어             "일주하우스 B동"       → B
    //      (한글은 1글자만 — "현대 봉천동" 같은 동네 이름을 동으로 오인하지 않도록)
    //   ③ 그래도 없으면 대지권(VWorld)에서 고른 동  selectedVworldHoItem.buldDongNm "비" → 비
    //   제외: building-info 가 동 이름이 없을 때 만든 표시용 이름
    //     ("공동주택 890.11㎡", "봉천동 다중주택", "주건축물제1동") → 빈칸 = 기존 값 유지
    //   '동' 글자는 빼고 넘김: 매물장 화면이 "{동}동" 으로 붙여 보여줌 (PropertyList.js)
    //   받는 쪽: EstateEditor.jsx applyBuildingInfoToDetail() estate_dong (적용=빈칸만, 교체=덮어씀)
    const dongForApply = (() => {
      const norm = (v, oneHangul) => {
        const t = String(v || "").trim();
        const re = oneHangul
          ? /^제?([0-9]{1,4}|[A-Za-z]{1,2}|[가-힣])동?$/
          : /^제?([0-9]{1,4}|[A-Za-z]{1,2}|[가-힣]{1,2})동?$/;
        const m = t.match(re);
        return m ? m[1].toUpperCase() : "";
      };
      const raw = String(selectedDong || "").trim();
      let d = norm(raw, false);                                         // ①
      if (!d && /\s/.test(raw)) d = norm(raw.split(/\s+/).pop(), true); // ②
      if (!d) d = norm(selectedVworldHoItem?.buldDongNm, false);         // ③
      return d;
    })();

    const formattedUseAprDay = (() => {
      const raw = selectedDongItem?.useAprDay || "";
      if (raw.length === 8 && !raw.includes("-")) {
        return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
      }
      return raw;
    })();

    const rentHouseholdTotal =
      (Number(selectedDongItem?.hhldCnt) || 0) +
      (Number(selectedDongItem?.fmlyCnt) || 0) +
      (Number(selectedDongItem?.hoCnt) || 0);
      
    return {
      // [BLD_PURPOSE_V1] 주용도 — 받는 쪽: EstateEditor.jsx applyBuildingInfoToDetail()
      //   ho   : 선택한 호(전유부)의 주용도  (호를 고르지 않았으면 "")
      //   main : 건물(표제부·동) 주용도      etc : 건물 상세(기타)용도
      purpose: {
        ho:   selectedHoItem?.mainPurpsCdNm || "",
        main: selectedDongItem?.mainPurpsCdNm || recap?.mainPurpsCdNm || "",
        etc:  selectedDongItem?.etcPurps || recap?.etcPurps || "",
      },
      building: {
        name: recap?.bldNm || "",
        dong: dongForApply,
        ho: selectedHo || "",
        floor: floorNumeric,
        completionDate: formattedUseAprDay,
        completion_year_override: formattedUseAprDay, // 🔥 강제 교체용
      },
      area: {
        private: selectedHoItem?.privateArea ?? null,
        public: selectedHoItem?.publicArea ?? null,
        total: selectedDongItem?.totArea ?? null,
        land: landArea,
      },
      buildingStats: {
        groundFloors: selectedDongItem?.grndFlrCnt ?? null,
        undergroundFloors: selectedDongItem?.ugrndFlrCnt ?? null,
        elevatorCount: selectedDongItem?.elevCnt ?? null, // preview용
        parkingTotal: recap?.parking?.tot ?? null,        // preview용
        householdCount: rentHouseholdTotal, // preview용

        // ⬇ EstateEditor 적용용 (중복 주입)
        parking_cnt: recap?.parking?.tot ?? null,
        rent_household_cnt: rentHouseholdTotal, // ✅ 최종 저장값
        living_condition_ev:
          selectedDongItem?.elevCnt > 0 ? "y" : "n",
        bld_scale_under: selectedDongItem?.ugrndFlrCnt ?? null,
      },
      // [USE_DISTRICT_AUTO_V1] 대장 용도지역 이름 목록 → EstateEditor 가 코드(ud14 등)로 바꿔 use_district 에 입력
      //   jijiguList 는 이 컴포넌트 위쪽(화면 표시용)에서 만든 배열과 같은 데이터
      zoning: {
        names: jijiguList.map((j) => (j && (j.name || j.etc)) || "").filter(Boolean),
      },
      landShare: selectedVworldHoItem
        ? {
            value: selectedVworldHoItem.landShare,
            total: selectedVworldHoItem.landTotal,
          }
        : null,
      location: {
        lat: mapLat,
        lng: mapLng,
        station: nearestStation
          ? {
              name: nearestStation.station_name,
              walkMin: nearestStation.walk_min,
              distanceM: nearestStation.distance_m,
            }
          : null,
      },
    };
  };

  console.log("LINES RAW:", nearestStation?.lines);

  return (
    <div className="building-overlay-backdrop">
      <div className="building-overlay-panel" style={{ display: 'flex', flexDirection: 'column', height: '90vh' }}>
        <div className="building-overlay-header">
          <h3>건물정보 확인</h3>

          {/* ✅ 캐시 상태 표시 + 최신정보 조회 버튼 */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            {cachedAt ? (
              <span style={{ fontSize: 11, color: "#888", background: "#f0f0f0", padding: "2px 6px", borderRadius: 3 }}>
                📦 저장된 데이터 ({new Date(cachedAt).toLocaleDateString("ko-KR")})
              </span>
            ) : (
              building && !loading && (
                <span style={{ fontSize: 11, color: "#2b6cb0" }}>🔄 실시간 조회</span>
              )
            )}
            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={loading}
                style={{
                  padding: "3px 10px",
                  fontSize: 11,
                  background: "#e6f0ff",
                  border: "1px solid #4a90d9",
                  borderRadius: 4,
                  cursor: loading ? "not-allowed" : "pointer",
                  color: "#2b5797",
                  opacity: loading ? 0.6 : 1,
                }}
              >
                🔃 최신정보 조회
              </button>
            )}
          </div>

          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            {!loading && building && (
              <button
                type="button"
                className="overlay-replace-btn"
                onClick={() => {
                  const data = buildApplyData();
                  console.log("[APPLY DATA BUILDING STATS]", data.buildingStats);
                  if (!data) return;
                  setApplyData(data);
                  setConfirmOpen(true);
                }}
                disabled={loading || !building}
                style={{
                  padding: "6px 12px",
                  backgroundColor: "#b91c1c",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "12px",
                  cursor: "pointer"
                }}
              >
                건물정보 교체
              </button>
            )}

            {confirmOpen && (
              <div className="overlay-confirm-backdrop">
                <div className="overlay-confirm-panel">
                  <h4>건물정보 교체 확인</h4>
                  <p style={{ color: "#b91c1c" }}>
                    아래 항목들이 <b>기존 값과 무관하게 덮어씌워집니다</b>.
                  </p>

                  <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                    <tbody>
                      {previewRows.length === 0 ? (
                        <tr>
                          <td colSpan={2} style={{ padding: 8, color: "#999" }}>
                            적용할 데이터가 없습니다.
                          </td>
                        </tr>
                      ) : previewRows.map(([k, v], i) => (
                        <tr key={i}>
                          <td style={{ padding: "6px 8px", borderBottom: "1px solid #eee", width: 120 }}>{k}</td>
                          <td style={{ padding: "6px 8px", borderBottom: "1px solid #eee" }}>{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
                    <button type="button" onClick={() => setConfirmOpen(false)}>취소</button>
                    <button
                      type="button"
                      style={{ background: "#b91c1c", color: "#fff" }}
                      onClick={() => {
                        if (!applyData) return;
                        if (onApply) onApply(applyData, "override");
                        if (onClose) onClose();
                      }}
                    >
                      교체 실행
                    </button>
                  </div>
                </div>
              </div>
            )}

            {!loading && building && (
              <button
                type="button"
                className="overlay-save-btn"
                onClick={() => {
                  const data = buildApplyData();
                  if (!data) return;
                  setApplyData(data);
                  if (onApply) onApply(data, "ifEmpty");
                  if (onClose) onClose();
                }}
                disabled={loading || !building}
                style={{
                  padding: "6px 12px",
                  backgroundColor: "#2b6cb0",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "12px",
                  cursor: "pointer"
                }}
              >
                건물정보 적용
              </button>
            )}


            <button
              type="button"
              className="overlay-close-btn"
              onClick={onClose}
            >
              닫기
            </button>
          </div>
        </div>

        <div className="building-overlay-body" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%' }}>
          {/* 🔹 상단 고정 영역: 주소, 지도, 역 정보 */}
          <div className="fixed-top-section" style={{ flexShrink: 0, paddingBottom: "10px", borderBottom: "1px solid #eee" }}>
            <div className="building-overlay-address">
              <span>조회주소: </span>
              <strong>{displayAddress || "-"}</strong>
            </div>

            {mapLat && mapLng && (
              <div
                id="building-info-map"
                style={{
                  width: "100%",
                  height: "180px", // 높이 약간 조절
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  marginTop: "10px",
                }}
              ></div>
            )}

            {nearestStation && (
              <div className="building-info-station" style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "10px", flexWrap: "wrap" }}>
                <div style={{ display: "flex", flexDirection: "row", gap: "5px" }}>
                  {(nearestStation?.lines || []).map((ln, idx) => {
                    const norm = normalizeLineName(ln) || "UNKNOWN";
                    const bg = LINE_COLORS[norm] || "#333";
                    return (
                      <div
                        key={idx}
                        style={{
                          backgroundColor: bg,
                          color: "#fff",
                          padding: "4px 10px",
                          borderRadius: "6px",
                          fontSize: "13px",
                          display: "inline-flex",
                          alignItems: "center",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {nearestStation.station_name}
                      </div>
                    );
                  })}
                </div>

                <div className="station-detail" style={{ fontSize: "13px", color: "#666" }}>
                  {nearestStation.distance_m}m · 도보 {nearestStation.walk_min}분
                </div>
              </div>
            )}
          </div>

          {/* 🔹 하단 스크롤 영역: 상세 건물 정보 */}
          <div className="scrollable-content-section" style={{ flex: 1, overflowY: "auto", paddingTop: "15px" }}>
          
            {loading && (
              <div className="building-overlay-status">
                건물정보 조회 중...
              </div>
            )}

            {error && !loading && (
              <div className="building-overlay-error">오류: {error}</div>
            )}

            {!loading && !error && !building && (
              <div className="building-overlay-status">
                조회된 건물정보가 없습니다.
              </div>
            )}

            {!loading && !error && building && (
              <div className="building-overlay-content">
                {/* 상단 요약 정보 */}
                <div className="building-summary">
                  <div className="detail-row">
                    <span className="label">건물명</span>
                    <span className="value">{recap.bldNm || "-"}</span>
                  </div>

                  <div className="detail-row">
                    <span className="label">주용도</span>
                    <span className="value">{recap.mainPurpsCdNm || "-"}</span>
                  </div>

                  <div className="detail-row">
                    <span className="label">용도지역</span>
                    <textarea
                      readOnly
                      value={landUseText}
                      style={{
                        width: "150px",
                        height: "60px",
                        resize: "none",
                        fontSize: "12px",
                        padding: "6px",
                        lineHeight: "1.4",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        background: "#f9f9f9",
                      }}
                    />
                  </div>

                  {/* 층별개요 폴다운 리스트 */}
                  <div className="floor-outline-section" style={{ marginBottom: "15px" }}>
                    <button 
                      type="button"
                      className="toggle-details-btn"
                      onClick={() => setShowFloorDetails(!showFloorDetails)}
                      style={{ fontSize: "12px", color: "#2b6cb0", cursor: "pointer", background: "none", border: "none", padding: 0 }}
                    >
                      {showFloorDetails ? "▲ 층별개요 닫기" : "▼ 층별개요 상세보기"}
                    </button>
                    
                    {showFloorDetails && selectedDongItem?.floorOutlines && (() => {
                      // 🔹 1. 프론트엔드에서 즉석 정렬 수행
                      // 우선순위: "지"(0) > 숫자(1) > 기타(2) > "옥/다"(3)
                      const sortedFloorOutlines = [...selectedDongItem.floorOutlines]
                        .map(f => ({ ...f, __sort: buildHoSortMeta(f.flrNoNm) }))
                        .sort((a, b) => {
                          if (a.__sort.priority !== b.__sort.priority) return a.__sort.priority - b.__sort.priority;
                          return a.__sort.key - b.__sort.key;
                        });

                      // 🔹 2. 정렬된 리스트를 테이블로 렌더링하여 반환
                      return (
                        <div className="floor-outline-list" style={{ marginTop: "6px", background: "#f8f9fa", padding: "6px", borderRadius: "4px", fontSize: "10.5px" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                              <tr style={{ borderBottom: "1px solid #ddd" }}>
                                <th align="left">층명</th>
                                <th align="left">주용도</th>
                                <th align="right">면적(㎡)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortedFloorOutlines.map((f, idx) => (
                                <tr key={idx} style={{ borderBottom: "1px solid #eee" }}>
                                  <td>{f.flrNoNm}</td>
                                  <td>{f.mainPurpsCdNm || f.etcPurps}</td>
                                  <td align="right">
                                    {typeof f.area === 'number' ? f.area.toFixed(2) : f.area}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}
                  </div>      

                  <div className="detail-row">
                    <span className="label">층수</span>
                    {/* 층수 */}
                    <span className="value">
                      지상 {selectedDongItem?.grndFlrCnt ?? "-"}
                      {selectedDongItem?.ugrndFlrCnt > 0 && ` / 지하 ${selectedDongItem.ugrndFlrCnt}`}
                    </span>
                  </div>

                  <div className="detail-row">
                    <span className="label">승강기</span>
                    <span className="value">
                      {selectedDongItem?.elevCnt ?? "-"}
                    </span>
                  </div>

                  <div className="detail-row">
                    <span className="label">주차장</span>
                    <span className="value">
                      {recap.parking?.tot ?? "-"}
                    </span>
                  </div>

                  <div className="detail-row">
                    <span className="label">해당건물 세대수</span>
                    <span className="value">
                        {(() => {
                          const counts = [];
                          if (selectedDongItem?.hhldCnt > 0) counts.push(`${selectedDongItem.hhldCnt}세대`);
                          if (selectedDongItem?.fmlyCnt > 0) counts.push(`${selectedDongItem.fmlyCnt}가구`);
                          if (selectedDongItem?.hoCnt > 0) counts.push(`${selectedDongItem.hoCnt}호`);
                          
                          return counts.length > 0 ? counts.join(" / ") : "-";
                        })()}
                      </span>
                  </div>

                  <div className="detail-row">
                    <span className="label">연면적</span>
                    {/* 연면적 */}
                    <span className="value">
                      {selectedDongItem?.totArea ?? "-"}
                    </span>
                  </div>

                  <div className="detail-row">
                    <span className="label">대지면적</span>
                    <span className="value">{landArea} ㎡</span>
                  </div>

                  <div className="detail-row">
                    <span className="label">준공일</span>
                    {/* 준공일 */}
                    <span className="value">
                      <span style={{ color: "#b91c1c", fontWeight: 600 }}>
                        {formatKoreanDate(selectedDongItem?.useAprDay)}
                      </span>
                    </span>
                  </div>

                  <div className="detail-row">
                    <span className="label">관계지번</span>
                    <span className="value">
                      {recap?.atchJibun?.length > 0 ? (
                        <span style={{ color: "#2638d1ff", fontWeight: 600 }}>
                          {recap.atchJibun.join(", ")}
                        </span>
                      ) : "-"}
                    </span>
                  </div>

                  {/* 건축물대장 동/호 선택 UI 통합 및 스타일 수정 */}
                  <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                    <span className="label">호수</span>
                    <div className="value" style={{ width: '100%', display: 'flex', gap: '5px' }}>
                      {/* 동 선택 */}
                      <select
                        value={selectedDong}
                        onChange={(e) => {
                          setSelectedDong(e.target.value);
                          setSelectedHo(""); 
                        }}
                        style={{ flex: 1, padding: '4px', fontSize: '12px' }}
                      >
                        {dongList.map((d) => (
                          <option key={d.dongName} value={d.dongName}>
                            {d.dongName}
                          </option>
                        ))}
                      </select>

                      {/* 호 선택 (하위 호수) */}
                      <select
                        value={selectedHo || ""}
                        onChange={(e) => setSelectedHo(e.target.value)}
                        disabled={readonly}
                        style={{ flex: 1, padding: '4px', fontSize: '12px' }}
                      >
                        <option value="">호 선택</option>
                        {sortedHos.map((h) => (
                          <option key={h.hoNm} value={h.hoNm}>
                            {h.hoNm}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="detail-row">
                    <span className="label">해당호 주용도</span>
                    {/* 해당호 주용도 */}
                    <span className="value">
                      {selectedHoItem?.mainPurpsCdNm ?? "-"}
                    </span>
                  </div>

                  <div className="detail-row">
                    <span className="label">전용면적(㎡)</span>
                    <span className="value">
                      {selectedHoItem &&
                      selectedHoItem.privateArea != null
                        ? selectedHoItem.privateArea
                        : "-"}
                    </span>
                  </div>

                  <div className="detail-row">
                    <span className="label">공용면적(㎡)</span>
                    <span className="value">
                      {selectedHoItem &&
                      selectedHoItem.publicArea != null
                        ? selectedHoItem.publicArea
                        : "-"}
                    </span>
                  </div>

                  <div className="detail-row">
                    <span className="label">해당층</span>
                    {/* 해당층 */}
                    <span className="value">
                      {selectedHoItem?.floorInfo ?? "-"}
                    </span>
                  </div>

                    {/* 해당층 정보 하단에 지분 선택 폴다운 리스트 추가 */}
                    <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                      <span className="label">대지권 지분</span>
                      <div className="value" style={{ width: '100%', display: 'flex', gap: '5px' }}>
                        {/* Vworld 동 선택 */}
                        <select 
                          value={vworldDong} 
                          onChange={(e) => { 
                            setVworldDong(e.target.value); 
                            setVworldHo(""); // 🔹 여기서도 setSelectedHo를 쓰고 있다면 setVworldHo로 수정
                          }}
                          style={{ flex: 1, padding: '4px', fontSize: '12px' }}
                        >
                          <option value="">동 선택</option>
                          {processedVworldList.map(d => (
                            <option key={d.dongName} value={d.dongName}>{d.dongName}</option>
                          ))}
                        </select>

                        {/* Vworld 호 선택 (폴다운 리스트) */}
                        <select 
                          value={vworldHo} 
                          onChange={(e) => setVworldHo(e.target.value)}
                          style={{ flex: 1, padding: '4px', fontSize: '12px' }}
                          disabled={!selectedVworldDongItem}
                        >
                          <option value="">호 선택</option>
                          {selectedVworldDongItem?.units.map(h => (
                            <option key={h.hoNm} value={h.hoNm}>{h.hoNm}</option>
                          ))}
                        </select>
                      </div>

                      {/* 선택된 지분 결과 출력 */}
                      <div className="share-result-value" style={{ width: '100%', marginTop: '4px', padding: '10px', background: '#f0f4f8', borderRadius: '4px' }}>
                        {selectedVworldHoItem ? (
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: '12px', color: '#666' }}>계산된 지분율:</span>
                              <span style={{ fontWeight: 'bold', color: '#2b6cb0' }}>
                                {selectedVworldHoItem.landShare} / {selectedVworldHoItem.landTotal} ㎡
                              </span>
                            </div>

                            {/* 여러 필지에 걸친 건물이면 필지별 내역을 펼쳐 보여줍니다.
                                (예: 닷컴힐즈 701호 = 1653-3 13.23/420 + 1653-4 2.6/82.4 + 1653-5 2.63/83.7) */}
                            {selectedVworldHoItem.parcelCount > 1 && (
                              <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed #cbd5e0' }}>
                                <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>
                                  필지 {selectedVworldHoItem.parcelCount}개 합산 내역
                                </div>
                                {selectedVworldHoItem.parcels.map((p, i) => (
                                  <div key={i} style={{
                                    display: 'flex', justifyContent: 'space-between',
                                    fontSize: '11px', color: '#4a5568', padding: '2px 0'
                                  }}>
                                    <span>{p.jibun}</span>
                                    <span>{p.rate} ㎡</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: '#999', fontSize: '12px' }}>동/호를 선택하면 지분이 표시됩니다.</span>
                        )}
                      </div>

                    </div>

                  <div className="detail-row">
                    <span className="label">동일용도 세대수</span>
                    {/* 동일용도 세대수 */}
                    <span className="value">
                      {selectedHoItem?.sameUsageTotalCount ?? "-"}
                    </span>
                  </div>
                  
                </div>

              </div>
            )}

          </div>

        </div>

      </div>

    </div>
  );
}